import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import AdminSidebar from '@/components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Search, User, MessageSquare, FileText, Plus } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomers = () => {
  const { getAuthHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers`, getAuthHeader());
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/customers/${customerId}/notes`, getAuthHeader());
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const handleViewNotes = async (customer) => {
    setSelectedCustomer(customer);
    await fetchNotes(customer.id);
    setNotesDialogOpen(true);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API_URL}/customers/${selectedCustomer.id}/notes`, 
        { content: newNote }, 
        getAuthHeader()
      );
      toast.success('Note added successfully');
      await fetchNotes(selectedCustomer.id);
      setNewNote('');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-customers">
      <AdminSidebar />
      
      <main className="flex-1 p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Customers</h1>
          <p className="text-slate-500">Manage customer records and notes</p>
        </div>

        <Card className="bg-white">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>All Customers</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="customer-search"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-slate-500">Loading...</div>
            ) : filteredCustomers.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <User className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No customers found</p>
              </div>
            ) : (
              <Table className="table-modern">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.phone || '-'}</TableCell>
                      <TableCell>{format(new Date(customer.created_at), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewNotes(customer)}
                            data-testid={`view-notes-${customer.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Notes
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Notes Dialog */}
        <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="notes-dialog">
            <DialogHeader>
              <DialogTitle>Notes for {selectedCustomer?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Add Note */}
              <div className="space-y-2">
                <Label>Add Note</Label>
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Enter note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={2}
                    data-testid="new-note-input"
                  />
                </div>
                <Button onClick={handleAddNote} size="sm" data-testid="add-note-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Note
                </Button>
              </div>

              {/* Notes List */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Previous Notes</h4>
                {notes.length === 0 ? (
                  <p className="text-slate-500 text-sm">No notes yet</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {notes.map((note) => (
                      <div key={note.id} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-700">{note.content}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {note.created_by} • {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminCustomers;
